import { getFirebaseBackend } from '../../authUtils.js'

export const state = {
    currentUser: loadStorageState('authUser', null),
    csrfPreventionToken: loadStorageState('auth.csrfPreventionToken', null),
    apiCurrentUser: loadStorageState('auth.apiCurrentUser', null),
    apiAccessFlags: loadStorageState('auth.apiAccessFlags', {
        isAdmin: null,
        selfcareVmAccess: null,
        selfcareKubernetesAccess: null,
        selfcareFinancialAccess: null,
    }),
}

export const mutations = {
    SET_CURRENT_USER(state, newValue) {
        state.currentUser = newValue
        saveStorageState('authUser', newValue)
    },
    SET_CSRF_PREVENTION_TOKEN(state, token) {
        state.csrfPreventionToken = token || null
        saveStorageState('auth.csrfPreventionToken', state.csrfPreventionToken)
    },
    SET_API_CURRENT_USER(state, user) {
        state.apiCurrentUser = user || null
        saveStorageState('auth.apiCurrentUser', state.apiCurrentUser)
    },
    SET_API_ACCESS_FLAGS(state, flags) {
        const source = flags && typeof flags === 'object' ? flags : {}
        state.apiAccessFlags = {
            isAdmin: normalizeNullableBoolean(source.isAdmin),
            selfcareVmAccess: normalizeNullableBoolean(source.selfcareVmAccess),
            selfcareKubernetesAccess: normalizeNullableBoolean(source.selfcareKubernetesAccess),
            selfcareFinancialAccess: normalizeNullableBoolean(source.selfcareFinancialAccess),
        }
        saveStorageState('auth.apiAccessFlags', state.apiAccessFlags)
    },
    MERGE_API_ACCESS_FLAGS_FROM_PROFILE(state, user) {
        const source = user && typeof user === 'object' ? user : {}
        const current = state.apiAccessFlags && typeof state.apiAccessFlags === 'object'
            ? state.apiAccessFlags
            : {
                isAdmin: null,
                selfcareVmAccess: null,
                selfcareKubernetesAccess: null,
                selfcareFinancialAccess: null,
            }

        state.apiAccessFlags = {
            isAdmin: resolveProfileOverride(current.isAdmin, source, 'isAdmin'),
            selfcareVmAccess: resolveProfileOverride(current.selfcareVmAccess, source, 'selfcareVmAccess'),
            selfcareKubernetesAccess: resolveProfileOverride(current.selfcareKubernetesAccess, source, 'selfcareKubernetesAccess'),
            selfcareFinancialAccess: resolveProfileOverride(current.selfcareFinancialAccess, source, 'selfcareFinancialAccess'),
        }
        saveStorageState('auth.apiAccessFlags', state.apiAccessFlags)
    },
    CLEAR_API_SESSION(state) {
        state.csrfPreventionToken = null
        state.apiCurrentUser = null
        state.apiAccessFlags = {
            isAdmin: null,
            selfcareVmAccess: null,
            selfcareKubernetesAccess: null,
            selfcareFinancialAccess: null,
        }
        clearStorageState('auth.csrfPreventionToken')
        clearStorageState('auth.apiCurrentUser')
        clearStorageState('auth.apiAccessFlags')
    }
}

export const getters = {
    // Whether the user is currently logged in.
    loggedIn(state) {
        return !!state.currentUser
    },
    csrfPreventionToken(state) {
        return state.csrfPreventionToken
    },
    apiCurrentUser(state) {
        return state.apiCurrentUser
    },
    apiAccessFlags(state) {
        return state.apiAccessFlags || {}
    },
    isApiAuthenticated(state) {
        return !!state.csrfPreventionToken
    },
    hasApiAccessFlags(state) {
        const flags = state.apiAccessFlags || {}
        return ['isAdmin', 'selfcareVmAccess', 'selfcareKubernetesAccess', 'selfcareFinancialAccess']
            .some((key) => typeof flags[key] === 'boolean')
    },
    // Access to the "Virtuelle Infrastruktur" / VM features.
    canAccessVm(state, getters) {
        const flags = getters.apiAccessFlags
        return Boolean(flags.isAdmin || flags.selfcareVmAccess)
    },
    // Admin-only features.
    canAccessAdmin(state, getters) {
        const flags = getters.apiAccessFlags
        return Boolean(flags.isAdmin)
    },
    // Access to the Kubernetes features.
    canAccessKubernetes(state, getters) {
        const flags = getters.apiAccessFlags
        return Boolean(flags.isAdmin || flags.selfcareKubernetesAccess)
    },
    // Access to financial features.
    canAccessFinancial(state, getters) {
        const flags = getters.apiAccessFlags
        return Boolean(flags.isAdmin || flags.selfcareFinancialAccess)
    },
}

export const actions = {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    // eslint-disable-next-line no-unused-vars
    init({ state, dispatch }) {
        dispatch('validate')
    },

    // Logs in the current user.
    logIn({ commit, dispatch, getters }, { email, password } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getFirebaseBackend().loginUser(email, password).then((response) => {
            const user = response
            commit('SET_CURRENT_USER', user)
            return user
        });
    },

    // Logs out the current user.
    logOut({ commit }) {
        // eslint-disable-next-line no-unused-vars
        commit('SET_CURRENT_USER', null)
        return new Promise((resolve, reject) => {
            // eslint-disable-next-line no-unused-vars
            getFirebaseBackend().logout().then((response) => {
                resolve(true);
            }).catch((error) => {
                reject(this._handleError(error));
            })
        });
    },

    // register the user
    register({ commit, dispatch, getters }, { username, email, password } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getFirebaseBackend().registerUser(username, email, password).then((response) => {
            const user = response
            commit('SET_CURRENT_USER', user)
            return user
        });
    },

    // register the user
    // eslint-disable-next-line no-unused-vars
    resetPassword({ commit, dispatch, getters }, { email } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getFirebaseBackend().forgetPassword(email).then((response) => {
            const message = response.data
            return message
        });
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    // eslint-disable-next-line no-unused-vars
    validate({ commit, state }) {
        if (!state.currentUser) return Promise.resolve(null)
        const user = getFirebaseBackend().getAuthenticatedUser();
        commit('SET_CURRENT_USER', user)
        return user;
    },

    setApiSession({ commit }, { csrfPreventionToken } = {}) {
        commit('SET_CSRF_PREVENTION_TOKEN', csrfPreventionToken)
    },

    setApiCurrentUser({ commit }, user) {
        commit('SET_API_CURRENT_USER', user)
    },

    setApiAccessFlags({ commit }, flags) {
        commit('SET_API_ACCESS_FLAGS', flags)
    },

    applyProfileAccessFlags({ commit }, user) {
        commit('MERGE_API_ACCESS_FLAGS_FROM_PROFILE', user)
    },

    clearApiSession({ commit }) {
        commit('CLEAR_API_SESSION')
    },
}

// ===
// Private helpers
// ===


function saveStorageState(key, value) {
    if (value === null || value === undefined) {
        clearStorageState(key)
        return
    }
    window.localStorage.setItem(key, JSON.stringify(value))
}

function loadStorageState(key, fallback = null) {
    const raw = window.localStorage.getItem(key)
    if (!raw) return fallback
    try {
        return JSON.parse(raw)
    } catch (_) {
        return fallback
    }
}

function clearStorageState(key) {
    window.localStorage.removeItem(key)
}

function normalizeNullableBoolean(value) {
    if (value === null || value === undefined) return null
    if (typeof value === 'boolean') return value
    if (value === 1 || value === 0) return value === 1
    const normalized = String(value).trim().toLowerCase()
    if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') return true
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') return false
    return null
}

function resolveProfileOverride(currentValue, source, key) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) return normalizeNullableBoolean(currentValue)
    const normalized = normalizeNullableBoolean(source[key])
    if (normalized === null) return normalizeNullableBoolean(currentValue)
    return normalized
}
